import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { ValidationError } from "yup";
import { Check } from "lucide-react";
import { Currency, HeyLightProductDTO } from "./useHeyLightProductById.ts";
import { BusinessType } from "../MoneyTransfer/BusinessType.tsx";
import {
  CheckBox,
  PrimaryButton,
  Select,
  TextInput,
} from "@normasteaching/norma-ui";
import { CheckoutPhoneInput } from "../NormasTeachingCheckout/CheckoutPhoneInput.tsx";
import { useSearchParams } from "react-router";
import { parsePhoneNumberIfDefined } from "./parsePhoneNumberIfDefined.ts";
import { CustomerInformation } from "../NormasTeachingCheckout/CheckoutData.ts";

export type ParticipantData = {
  firstName: string;
  lastName: string;
  email: string;
};

export type HeyLightCheckoutFormData = {
  email: string;
  name: string;
  surname: string;
  phoneNumber: {
    countryCode: string;
    number: string;
    dialCode: string;
  };
  invoiceRequired: boolean;
  businessType?: string | undefined;
  pec?: string | undefined;
  sdiCode?: string | undefined;
  vat?: string | undefined;
  privacyPolicyAccepted: boolean;
  additionalTerms: boolean;
  participants: ParticipantData[] | [];
  bundleParticipants:
    | {
        products: {
          id: string;
          priceInCents: { amount: number; currency: string };
        }[];
        customer: CustomerInformation;
      }[]
    | [];
  coupon?: {
    _id: string;
    couponCode: string;
    value: number;
  };
  promos?: Array<{
    _id: string;
    bonuses: Array<
      | {
          type: "all_you_can_speak";
          lessonsCountedPerWeek: number;
          activeFrom?: string;
          activeTo?: string;
        }
      | { type: "extra_lessons"; extraLessons: number }
      | { type: "extra_weeks"; extraWeeks: number }
    >;
  }>;
};

const validationSchema = yup
  .object({
    email: yup.string().email("Email non valida").required("Email richiesta"),
    name: yup.string().min(2, "Nome troppo corto").required("Nome richiesto"),
    surname: yup
      .string()
      .min(2, "Cognome troppo corto")
      .required("Cognome richiesto"),
    phoneNumber: yup.object({
      dialCode: yup.string().required("Codice paese richiesto"),
      countryCode: yup.string().required("Codice paese richiesto"),
      number: yup
        .string()
        .matches(/^\d{6,15}$/, "Numero di telefono non valido")
        .required("Numero di telefono richiesto"),
    }),
    invoiceRequired: yup.boolean().default(false),
    businessType: yup.string().when("invoiceRequired", {
      is: true,
      then: (schema) =>
        schema
          .oneOf(
            [BusinessType.Person, BusinessType.IndividualCompany],
            "Tipo entità richiesto",
          )
          .required("Tipo entità richiesto"),
      otherwise: (schema) => schema.notRequired(),
    }),
    vat: yup.string().when(["invoiceRequired", "businessType"], {
      is: (invoiceRequired: boolean, businessType: string) =>
        invoiceRequired && businessType === BusinessType.IndividualCompany,
      then: (schema) => schema.required("Partita IVA richiesta"),
      otherwise: (schema) => schema.notRequired(),
    }),
    pec: yup.string().email().optional(),
    sdiCode: yup.string().optional(),
    privacyPolicyAccepted: yup
      .boolean()
      .required("Devi accettare la privacy policy")
      .oneOf([true], "Devi accettare la privacy policy"),
    additionalTerms: yup
      .boolean()
      .required("Devi accettare le clausole aggiuntive")
      .oneOf([true], "Devi accettare le clausole aggiuntive"),
    participants: yup
      .array()
      .of(
        yup.object({
          firstName: yup
            .string()
            .min(2, "Nome troppo corto")
            .required("Nome richiesto"),
          lastName: yup
            .string()
            .min(2, "Cognome troppo corto")
            .required("Cognome richiesto"),
          email: yup
            .string()
            .email("Email non valida")
            .required("Email richiesta"),
        }),
      )
      .default([]),
    bundleParticipants: yup
      .array()
      .of(
        yup.object({
          products: yup
            .array()
            .of(
              yup
                .object({
                  id: yup.string().required(),
                  priceInCents: yup.object({
                    amount: yup.number().required(),
                    currency: yup.string().required(),
                  }),
                })
                .required(),
            )
            .required()
            .default([]),
          customer: yup.object({
            firstName: yup.string().required("Nome richiesto"),
            lastName: yup.string().required("Cognome richiesto"),
            email: yup
              .string()
              .email("Email non valida")
              .required("Email richiesta"),
          }),
        }),
      )
      .default([]),
  })
  .test(
    "has-at-least-sdi-or-pec",
    "Inserisci almeno un codice SDI o PEC",
    (value, { createError }) => {
      if (!value.invoiceRequired) return true;
      if ([BusinessType.Person].includes(value.businessType as BusinessType))
        return true;
      const isValid = !!value.sdiCode || !!value.pec;
      if (!isValid) {
        const error = "Inserisci almeno un codice SDI o PEC";
        return createError({
          message: () => [
            new ValidationError(error, undefined, "sdiCode"),
            new ValidationError(error, undefined, "pec"),
          ],
        });
      }
      return isValid;
    },
  );

function mapCurrencyToSymbol(currency: Currency): string {
  switch (currency) {
    case Currency.Eur:
      return "€";
    default:
      return currency;
  }
}

function usePrefillValueFromQuery(): {
  email: string | undefined;
  firstName: string | undefined;
  lastName: string | undefined;
  phoneNumber:
    | { dialCode: string; countryCode: string; number: string }
    | undefined;
} {
  const [searchParams] = useSearchParams();

  const email = searchParams.get("em") ?? undefined;
  const firstName = searchParams.get("fn") ?? undefined;
  const lastName = searchParams.get("ln") ?? undefined;
  const phoneNumber = parsePhoneNumberIfDefined(
    "IT",
    searchParams.get("pn") ?? undefined,
  );

  return {
    email,
    firstName,
    lastName,
    phoneNumber,
  };
}

export const HeyLightCheckoutForm = ({
  product,
  onFormSubmit,
  submitting,
}: {
  product: HeyLightProductDTO;
  onFormSubmit: (data: HeyLightCheckoutFormData) => void;
  submitting: boolean;
}) => {
  const { email, firstName, lastName, phoneNumber } =
    usePrefillValueFromQuery();
  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors },
    trigger,
    clearErrors,
  } = useForm<HeyLightCheckoutFormData>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      invoiceRequired: false,
      phoneNumber: phoneNumber ?? {
        dialCode: "+39",
        countryCode: "IT",
        number: "",
      },
      businessType: "",
      pec: "",
      sdiCode: "",
      vat: "",
      email: email ?? "",
      name: firstName ?? "",
      surname: lastName ?? "",
    },
  });

  const watchedValues = watch();

  const pecControl = register("pec");
  const sdiCodeControl = register("sdiCode");

  const onSubmit = (data: HeyLightCheckoutFormData) => {
    onFormSubmit(data);
  };

  return (
    <div className="max-w-2xl mx-auto p-6 bg-white">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          {product.productName}
        </h1>
        <p className="text-lg text-gray-900 mb-4">
          {product.priceInCents.amount / 100}{" "}
          {mapCurrencyToSymbol(product.priceInCents.currency)}
        </p>
        <p className="text-gray-600 mb-2">
          Compila i tuoi dati anagrafici per accedere alla dilazione di
          pagamento attraverso la finanziaria.
        </p>
      </div>

      <div className="space-y-6">
        {/* Nome */}
        <div>
          <label className="block text-md font-bold text-gray-900 mb-2">
            Nome
          </label>
          <TextInput
            {...register("name")}
            errorMessage={errors.name?.message}
            placeholder="Il tuo nome"
          />
        </div>

        {/* Cognome */}
        <div>
          <label className="block text-md font-bold text-gray-900 mb-2">
            Cognome
          </label>
          <TextInput
            {...register("surname")}
            errorMessage={errors.surname?.message}
            placeholder="Il tuo cognome"
          />
        </div>

        {/* Email */}
        <div>
          <label className="block text-md font-bold text-gray-900 mb-2">
            Email
          </label>
          <p className={"mb-2 text-gray-600"}>
            Questo indirizzo email sarà utilizzato per accedere al corso e
            ricevere tutte le comunicazioni
          </p>
          <TextInput
            type="email"
            {...register("email")}
            errorMessage={errors.email?.message}
            placeholder="La tua email"
          />
        </div>

        {/* Numero di telefono */}
        <div>
          <label className="block text-md font-bold text-gray-900 mb-2">
            Numero di telefono
          </label>
          <Controller
            name="phoneNumber"
            control={control}
            render={({ field }) => (
              <CheckoutPhoneInput
                errorMessage={errors.phoneNumber?.number?.message}
                value={field.value}
                onChange={(v) => field.onChange(v)}
                onNumberChange={() => clearErrors("phoneNumber")}
              />
            )}
          />
        </div>

        {/* Partecipanti extra */}
        {product.peopleCount > 1 && (
          <div className="space-y-6 pt-6">
            <h3 className="text-lg font-bold text-gray-900">
              {product.peopleCount - 1 > 1
                ? "Dati partecipanti aggiuntivi"
                : "Dati partecipante aggiuntivo"}
            </h3>
            <p className={"mb-2 text-gray-600"}>
              Questo è un ordine di gruppo per {product.peopleCount} persone.
              Inserisci i dati degli altri partecipanti qui sotto.
            </p>

            {Array.from({ length: product.peopleCount - 1 }).map((_, index) => (
              <div
                key={index}
                className="border-gray-300 rounded-lg p-4 mb-4 bg-gray-50"
              >
                <h4 className="text-md font-semibold text-gray-700 mb-3">
                  Partecipante {index + 2}
                </h4>

                <div className="grid grid-cols-12 gap-4">
                  {/* Nome */}
                  <div className="col-span-12 md:col-span-3">
                    <label className="block text-md font-bold text-gray-900 mb-2">
                      Nome
                    </label>
                    <TextInput
                      {...register(`participants.${index}.firstName` as const)}
                      errorMessage={
                        errors.participants?.[index]?.firstName?.message
                      }
                      placeholder="Nome"
                    />
                  </div>

                  {/* Cognome */}
                  <div className="col-span-12 md:col-span-3">
                    <label className="block text-md font-bold text-gray-900 mb-2">
                      Cognome
                    </label>
                    <TextInput
                      {...register(`participants.${index}.lastName` as const)}
                      errorMessage={
                        errors.participants?.[index]?.lastName?.message
                      }
                      placeholder="Cognome"
                    />
                  </div>

                  {/* Email */}
                  <div className="col-span-12 md:col-span-6">
                    <label className="block text-md font-bold text-gray-900 mb-2">
                      Email
                    </label>
                    <TextInput
                      type="email"
                      {...register(`participants.${index}.email` as const)}
                      errorMessage={
                        errors.participants?.[index]?.email?.message
                      }
                      placeholder="Email"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Richiesta fattura */}
        <div id="richiestaFattura" className="pt-4">
          <div className="flex items-center gap-3">
            <Controller
              render={({ field }) => {
                return (
                  <CheckBox
                    label={<span>Desidero la fattura</span>}
                    value={field.value}
                    onChange={field.onChange}
                  />
                );
              }}
              name={"invoiceRequired"}
              control={control}
            />
          </div>
        </div>

        {/* Sezione fatturazione condizionale */}
        {watchedValues.invoiceRequired && (
          <div className="bg-gray-50 p-6 rounded-lg space-y-6">
            <h3 className="text-lg font-bold text-gray-900">
              Dati fatturazione
            </h3>

            {/* Tipo entità */}
            <div>
              <label className="block text-md font-bold text-gray-900 mb-2">
                Tipo entità
              </label>
              <p className={"mb-2"}>
                Puoi accedere alla dilazione di pagamento con finanziaria solo
                se sei una persona fisica o una ditta individuale. Nel caso di
                acquisti per SRL, SPA, SNC o SAS, contattaci a{" "}
                <a
                  href={`mailto:help@normasteaching.com?subject=Richiesta%20dilazione%20pagamento%20società&body=Salve,%0A%0Asono interessato ad acquistare il corso ${product.productName} ma sono una società. Vorrei sapere se è possibile accedere alla dilazione di pagamento con finanziaria`}
                  className={"underline text-blue-500"}
                >
                  help@normasteaching.com
                </a>
              </p>
              <Controller
                control={control}
                render={({ field }) => {
                  return (
                    <Select
                      errorMessage={errors.businessType?.message}
                      value={field.value}
                      onChange={field.onChange}
                      options={[
                        {
                          id: "",
                          name: "Seleziona tipo entità",
                        },
                        {
                          id: BusinessType.Person,
                          name: "Persona Fisica",
                        },
                        {
                          id: BusinessType.IndividualCompany,
                          name: "Ditta Individuale",
                        },
                      ]}
                    />
                  );
                }}
                name={"businessType"}
              />
            </div>

            {/* Campi per Ditta Individuale */}
            {watchedValues.businessType === BusinessType.IndividualCompany && (
              <div className="space-y-6">
                <div>
                  <label className="block text-md font-bold text-gray-900 mb-2">
                    Partita IVA
                  </label>
                  <TextInput
                    {...register("vat")}
                    errorMessage={errors.vat?.message}
                    placeholder="12345678901"
                  />
                </div>

                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm text-blue-800 font-medium">
                    Specificare almeno uno dei seguenti campi:
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-md font-bold text-gray-900 mb-2">
                      PEC
                    </label>
                    <TextInput
                      type="email"
                      {...pecControl}
                      onChange={async (e) => {
                        await pecControl.onChange(e);
                        await trigger(["pec", "sdiCode"]);
                      }}
                      errorMessage={errors.pec?.message}
                      placeholder="pec@example.com"
                    />
                  </div>

                  <div>
                    <label className="block text-md font-bold text-gray-900 mb-2">
                      Codice SDI
                    </label>
                    <TextInput
                      {...sdiCodeControl}
                      onChange={async (e) => {
                        await sdiCodeControl.onChange(e);
                        await trigger(["pec", "sdiCode"]);
                      }}
                      errorMessage={errors.sdiCode?.message}
                      placeholder="ABC1234"
                    />
                  </div>
                </div>
              </div>
            )}

            {watchedValues.businessType === BusinessType.Person && (
              <div className="bg-green-50 p-4 rounded-lg">
                <div className="flex items-center gap-2">
                  <Check className="h-5 w-5 text-green-600" />
                  <p className="text-sm text-green-800 font-medium">
                    Nessun dato aggiuntivo richiesto per persona fisica
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="pt-6 flex flex-col gap-2">
          <PrimaryButton
            loading={submitting}
            onClick={handleSubmit(onSubmit)}
            label={"Vai alla finanziaria"}
          />
          <div className={"flex justify-center"}>
            <Controller
              render={({ field }) => {
                return (
                  <CheckBox
                    label={
                      <span>
                        <a
                          className={"text-blue-500 underline"}
                          target={"_blank"}
                          rel={"noopener noreferrer"}
                          href="https://normasteaching.com/legal-info"
                        >
                          Accetto la privacy policy e i termini e condizioni
                        </a>
                      </span>
                    }
                    value={field.value}
                    onChange={field.onChange}
                    errorMessage={errors.privacyPolicyAccepted?.message}
                  />
                );
              }}
              name={"privacyPolicyAccepted"}
              control={control}
            />
          </div>
          <div className={"flex justify-center"}>
            <Controller
              render={({ field }) => {
                return (
                  <CheckBox
                    label={
                      <span>
                        <a
                          className={"text-blue-500 underline"}
                          target={"_blank"}
                          rel={"noopener noreferrer"}
                          href="https://normasteaching.com/clausole-vessatorie/"
                        >
                          Approvo specificatamente le seguenti clausole
                        </a>
                      </span>
                    }
                    value={field.value}
                    onChange={field.onChange}
                    errorMessage={errors.additionalTerms?.message}
                  />
                );
              }}
              name={"additionalTerms"}
              control={control}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
